// features/pos/components/ShiftModal.tsx
// ENHANCED - Better Visuals, Keyboard Input, Validation, Quick Amounts
// FIXED: Allow opening cash = 0

import { useState, useEffect, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Clock, Store, X, Calculator, AlertCircle, ArrowRight, Wallet } from 'lucide-react';
import { clsx } from 'clsx';

interface ShiftModalProps {
    isOpen: boolean;
    onClose: () => void;
    onConfirm: (openingCash: string) => void;
    isLoading: boolean;
    openingCash: string;
    onOpeningCashChange: (value: string) => void;
    branches?: any[];          // optional
    selectedBranchId?: string; // optional
    onBranchChange?: (id: string) => void; // optional
}

const QUICK_AMOUNTS = [
    { value: '0', label: '₦0' },
    { value: '5000', label: '₦5K' },
    { value: '10000', label: '₦10K' },
    { value: '20000', label: '₦20K' },
    { value: '50000', label: '₦50K' },
    { value: '100000', label: '₦100K' },
    { value: '200000', label: '₦200K' },
];

const CUSTOM_AMOUNTS = ['0', '500', '1000', '2000'];

export function ShiftModal({
    isOpen,
    onClose,
    onConfirm,
    isLoading,
    openingCash,
    onOpeningCashChange,
    branches = [],
    selectedBranchId = '',
    onBranchChange,
}: ShiftModalProps) {
    const [error, setError] = useState('');
    const [showCustomAmounts, setShowCustomAmounts] = useState(false);
    const inputRef = useRef<HTMLInputElement>(null);

    useEffect(() => {
        if (isOpen) {
            setError('');
            setShowCustomAmounts(false);
            setTimeout(() => inputRef.current?.focus(), 200);
        }
    }, [isOpen]);

    const handleConfirm = () => {
        const amount = parseFloat(openingCash);

        // Allow 0 or any positive number
        if (openingCash === '' || isNaN(amount) || amount < 0) {
            setError('Please enter a valid opening cash amount (0 or more).');
            inputRef.current?.focus();
            return;
        }

        if (amount > 10000000) {
            setError('Amount seems too high. Please verify.');
            return;
        }

        setError('');
        onConfirm(openingCash);
    };

    const handleKeyDown = (e: React.KeyboardEvent) => {
        if (e.key === 'Enter') {
            handleConfirm();
        }
        if (e.key === 'Escape') {
            onClose();
        }
    };

    const formatNaira = (val: string) => {
        const num = parseFloat(val || '0');
        if (isNaN(num)) return '₦0';
        return `₦${num.toLocaleString()}`;
    };

    // Helper to check if the current input is a valid non‑negative number
    const isValidAmount = openingCash !== '' && !isNaN(parseFloat(openingCash)) && parseFloat(openingCash) >= 0;

    return (
        <AnimatePresence>
            {isOpen && (
                <motion.div
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    exit={{ opacity: 0 }}
                    className="fixed inset-0 bg-black/60 backdrop-blur-sm z-50 flex items-center justify-center p-4"
                >
                    <motion.div
                        initial={{ scale: 0.95, opacity: 0, y: 20 }}
                        animate={{ scale: 1, opacity: 1, y: 0 }}
                        exit={{ scale: 0.95, opacity: 0, y: 20 }}
                        transition={{ type: 'spring', stiffness: 400, damping: 30 }}
                        className="bg-white rounded-3xl shadow-2xl w-full max-w-md overflow-hidden"
                        onKeyDown={handleKeyDown}
                    >
                        {/* Header */}
                        <div className="bg-gradient-to-br from-emerald-500 to-teal-600 px-8 py-8 text-center relative overflow-hidden">
                            {/* Decorative Background */}
                            <div className="absolute inset-0 opacity-10">
                                <div className="absolute top-0 right-0 w-32 h-32 bg-white rounded-full -translate-y-1/2 translate-x-1/2" />
                                <div className="absolute bottom-0 left-0 w-24 h-24 bg-white rounded-full translate-y-1/2 -translate-x-1/2" />
                            </div>

                            <motion.div
                                initial={{ scale: 0, rotate: -180 }}
                                animate={{ scale: 1, rotate: 0 }}
                                transition={{ type: 'spring', delay: 0.2 }}
                                className="relative z-10"
                            >
                                <div className="w-20 h-20 rounded-2xl bg-white/20 backdrop-blur-sm flex items-center justify-center mx-auto mb-4 ring-4 ring-white/20">
                                    <Clock size={36} className="text-white" />
                                </div>
                            </motion.div>

                            <motion.div
                                initial={{ opacity: 0, y: 10 }}
                                animate={{ opacity: 1, y: 0 }}
                                transition={{ delay: 0.3 }}
                                className="relative z-10"
                            >
                                <h2 className="text-2xl font-black text-white mb-1">Open Shift</h2>
                                <p className="text-emerald-100 text-sm">
                                    Start your cashier session for today
                                </p>
                            </motion.div>
                        </div>

                        <div className="p-6 space-y-5">
                            {/* Branch Selection - Only show if onBranchChange is provided */}
                            {branches.length > 0 && onBranchChange && (
                                <motion.div
                                    initial={{ opacity: 0, x: -10 }}
                                    animate={{ opacity: 1, x: 0 }}
                                    transition={{ delay: 0.35 }}
                                >
                                    <label className="flex items-center gap-2 text-xs font-bold text-slate-600 mb-2">
                                        <div className="p-1 rounded-lg bg-purple-100">
                                            <Store size={12} className="text-purple-600" />
                                        </div>
                                        Select Branch
                                    </label>
                                    <select
                                        value={selectedBranchId}
                                        onChange={(e) => onBranchChange(e.target.value)}
                                        className="w-full px-4 py-3 border-2 border-slate-200 rounded-xl text-sm bg-white focus:ring-2 focus:ring-emerald-500/20 focus:border-emerald-400 transition-all cursor-pointer"
                                    >
                                        {branches.map((b: any) => (
                                            <option key={b.id} value={b.id}>
                                                {b.isMainBranch ? '⭐ ' : ''}{b.name}
                                            </option>
                                        ))}
                                    </select>
                                </motion.div>
                            )}

                            {/* Show branch name without selector (for cashiers) */}
                            {!onBranchChange && selectedBranchId && (
                                <motion.div
                                    initial={{ opacity: 0, x: -10 }}
                                    animate={{ opacity: 1, x: 0 }}
                                    transition={{ delay: 0.35 }}
                                    className="flex items-center gap-2 p-3 bg-purple-50 border border-purple-200 rounded-xl"
                                >
                                    <div className="p-1.5 rounded-lg bg-purple-100">
                                        <Store size={14} className="text-purple-600" />
                                    </div>
                                    <div>
                                        <p className="text-[10px] font-bold text-purple-400 uppercase tracking-wider">Branch</p>
                                        <p className="text-sm font-semibold text-purple-700">
                                            {branches.find((b: any) => b.id === selectedBranchId)?.name || 'Selected Branch'}
                                        </p>
                                    </div>
                                </motion.div>
                            )}

                            {/* Opening Cash */}
                            <motion.div
                                initial={{ opacity: 0, x: -10 }}
                                animate={{ opacity: 1, x: 0 }}
                                transition={{ delay: 0.4 }}
                            >
                                <label className="flex items-center gap-2 text-xs font-bold text-slate-600 mb-2">
                                    <div className="p-1 rounded-lg bg-emerald-100">
                                        <Wallet size={12} className="text-emerald-600" />
                                    </div>
                                    Opening Cash Amount
                                </label>
                                <div className="relative">
                                    <span className="absolute left-4 top-1/2 -translate-y-1/2 text-2xl font-bold text-slate-300">
                                        ₦
                                    </span>
                                    <input
                                        ref={inputRef}
                                        type="number"
                                        value={openingCash}
                                        onChange={(e) => {
                                            onOpeningCashChange(e.target.value);
                                            setError('');
                                        }}
                                        className={clsx(
                                            'w-full pl-12 pr-4 py-5 border-2 rounded-2xl text-3xl font-bold text-center transition-all',
                                            'bg-white placeholder:text-slate-300',
                                            error
                                                ? 'border-rose-300 focus:ring-4 focus:ring-rose-500/10 focus:border-rose-400'
                                                : isValidAmount
                                                    ? 'border-emerald-300 focus:ring-4 focus:ring-emerald-500/10 focus:border-emerald-400'
                                                    : 'border-slate-200 focus:ring-4 focus:ring-emerald-500/10 focus:border-emerald-400'
                                        )}
                                        placeholder="0.00"
                                    />

                                    {/* Quick Calculator Toggle */}
                                    <button
                                        onClick={() => setShowCustomAmounts(!showCustomAmounts)}
                                        className="absolute right-3 top-1/2 -translate-y-1/2 p-2 rounded-xl hover:bg-slate-100 text-slate-400 transition-colors"
                                        type="button"
                                        title="Quick amounts"
                                    >
                                        <Calculator size={20} />
                                    </button>
                                </div>

                                {/* Live Preview - now shows for 0 as well */}
                                {isValidAmount && !error && (
                                    <motion.p
                                        initial={{ opacity: 0, y: -5 }}
                                        animate={{ opacity: 1, y: 0 }}
                                        className="text-center text-sm text-emerald-600 font-bold mt-2"
                                    >
                                        {formatNaira(openingCash)}
                                    </motion.p>
                                )}
                            </motion.div>

                            {/* Quick Amounts */}
                            <motion.div
                                initial={{ opacity: 0, y: 10 }}
                                animate={{ opacity: 1, y: 0 }}
                                transition={{ delay: 0.45 }}
                            >
                                <div className="grid grid-cols-3 gap-2">
                                    {QUICK_AMOUNTS.map(({ value, label }) => (
                                        <motion.button
                                            key={value}
                                            whileHover={{ scale: 1.05 }}
                                            whileTap={{ scale: 0.95 }}
                                            onClick={() => {
                                                onOpeningCashChange(value);
                                                setError('');
                                                setShowCustomAmounts(false);
                                            }}
                                            className={clsx(
                                                'px-3 py-2.5 rounded-xl border-2 text-sm font-bold transition-all',
                                                openingCash === value
                                                    ? 'border-emerald-400 bg-emerald-50 text-emerald-700 shadow-sm'
                                                    : 'border-slate-200 text-slate-600 hover:border-emerald-300 hover:bg-emerald-50/50'
                                            )}
                                            type="button"
                                        >
                                            {label}
                                        </motion.button>
                                    ))}
                                </div>
                            </motion.div>

                            {/* Custom Amounts */}
                            <AnimatePresence>
                                {showCustomAmounts && (
                                    <motion.div
                                        initial={{ opacity: 0, height: 0 }}
                                        animate={{ opacity: 1, height: 'auto' }}
                                        exit={{ opacity: 0, height: 0 }}
                                        className="overflow-hidden"
                                    >
                                        <div className="flex gap-2 flex-wrap">
                                            {CUSTOM_AMOUNTS.map(amt => (
                                                <motion.button
                                                    key={amt}
                                                    whileHover={{ scale: 1.05 }}
                                                    whileTap={{ scale: 0.95 }}
                                                    onClick={() => {
                                                        onOpeningCashChange(amt);
                                                        setError('');
                                                    }}
                                                    className={clsx(
                                                        'px-4 py-2 rounded-xl border-2 text-sm font-bold transition-all',
                                                        openingCash === amt
                                                            ? 'border-emerald-400 bg-emerald-50 text-emerald-700'
                                                            : 'border-slate-200 text-slate-500 hover:border-slate-300'
                                                    )}
                                                    type="button"
                                                >
                                                    ₦{parseInt(amt).toLocaleString()}
                                                </motion.button>
                                            ))}
                                        </div>
                                    </motion.div>
                                )}
                            </AnimatePresence>

                            {/* Error */}
                            <AnimatePresence>
                                {error && (
                                    <motion.div
                                        initial={{ opacity: 0, height: 0 }}
                                        animate={{ opacity: 1, height: 'auto' }}
                                        exit={{ opacity: 0, height: 0 }}
                                        className="flex items-center gap-2 p-3 bg-rose-50 border border-rose-200 rounded-xl text-sm text-rose-700"
                                    >
                                        <AlertCircle size={16} className="flex-shrink-0" />
                                        {error}
                                    </motion.div>
                                )}
                            </AnimatePresence>

                            {/* Action Buttons */}
                            <motion.div
                                initial={{ opacity: 0, y: 10 }}
                                animate={{ opacity: 1, y: 0 }}
                                transition={{ delay: 0.5 }}
                                className="flex gap-3 pt-2"
                            >
                                <motion.button
                                    whileHover={{ scale: 1.02 }}
                                    whileTap={{ scale: 0.98 }}
                                    onClick={onClose}
                                    className="flex-1 py-3.5 border-2 border-slate-200 rounded-xl font-semibold text-sm text-slate-600 hover:bg-slate-50 transition-all"
                                    type="button"
                                >
                                    Cancel
                                </motion.button>
                                <motion.button
                                    whileHover={{ scale: 1.02 }}
                                    whileTap={{ scale: 0.98 }}
                                    onClick={handleConfirm}
                                    // ✅ FIXED: Now enabled for zero and any non-negative number
                                    disabled={!isValidAmount || isLoading}
                                    className="flex-1 py-3.5 bg-gradient-to-r from-emerald-600 to-teal-600 text-white rounded-xl font-bold text-sm shadow-lg shadow-emerald-500/25 hover:from-emerald-700 hover:to-teal-700 disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:from-emerald-600 disabled:hover:to-teal-600 transition-all flex items-center justify-center gap-2"
                                    type="button"
                                >
                                    {isLoading ? (
                                        <>
                                            <motion.div
                                                animate={{ rotate: 360 }}
                                                transition={{ duration: 1, repeat: Infinity, ease: 'linear' }}
                                                className="w-4 h-4 rounded-full border-2 border-white border-t-transparent"
                                            />
                                            Opening...
                                        </>
                                    ) : (
                                        <>
                                            Open Shift
                                            <ArrowRight size={16} />
                                        </>
                                    )}
                                </motion.button>
                            </motion.div>

                            {/* Hint */}
                            <p className="text-center text-[10px] text-slate-400">
                                Press <kbd className="px-1.5 py-0.5 bg-slate-100 rounded text-[9px] font-mono">Enter</kbd> to confirm or <kbd className="px-1.5 py-0.5 bg-slate-100 rounded text-[9px] font-mono">Esc</kbd> to cancel
                            </p>
                        </div>
                    </motion.div>
                </motion.div>
            )}
        </AnimatePresence>
    );
}